import React, { ReactNode, useState } from 'react'

import Collapse from 'components_new/atoms/Collapse'
import Grid from 'components_new/atoms/Grid'
import Text from 'components_new/atoms/Text'

import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SettingsToggleOption from 'components_new/molecules/SettingsToggleOption'

import { translateShowPerEnum } from 'types/GlobalKpiOption'
import { ShowPerEnum } from 'types/GlobalMetric'

import { useTranslation } from 'language/translator'


const DISABLED_FOR_FINANCIAL_PERIOD = [
  ShowPerEnum.RENTED_SQM,
  ShowPerEnum.NUMBER_OF_SPACES,
  ShowPerEnum.NUMBER_OF_UNITS
]

interface DistributionSectionProps {
  distributions: { enum: ShowPerEnum; filter_options: string[] }[]

  // some distributions cannot follow a financial year period
  isFinancialYear: boolean
  onToggle: (distribution: ShowPerEnum | null) => void
  selected: ShowPerEnum | null

  // what the value is distributed over, for the option tooltips
  subject: string

  // NOT REQUIRED PROPS

  // the setting is decided elsewhere, and the text says where
  disabled?: boolean
  disabledText?: ReactNode
  filters?: {
    count: number
    content: ReactNode
  }
}

const DistributionSection = (props: DistributionSectionProps) => {
  const {
    disabled,
    disabledText,
    distributions,
    filters,
    isFinancialYear,
    onToggle,
    selected,
    subject
  } = props
  const [showMore, setShowMore] = useState<boolean>(false)

  const { t, translateEnum } = useTranslation()

  if (distributions.length === 0) {
    return null
  }

  const selectedDistribution = distributions.find(
    (opt) => opt.enum === selected
  )

  const showPerFilterOptions = selectedDistribution?.filter_options || []

  // an option's tooltip says what the value is distributed over, or
  // why the option cannot be picked
  const getOptionState = (
    option: ShowPerEnum
  ): { disabled: boolean; tooltip: ReactNode } => {
    const label = (
      <Text component="span" fontSize="inherit" fontWeight="bold">
        {translateEnum('show_pers', option).title}
      </Text>
    )

    if (disabled) {
      return { disabled: true, tooltip: disabledText }
    }

    if (DISABLED_FOR_FINANCIAL_PERIOD.includes(option) && isFinancialYear) {
      return {
        disabled: true,
        tooltip: (
          <>
            <Text component="span" fontSize="inherit">
              {t('widgets', 'disabled_for_financial_year')}{' '}
            </Text>
            {label}
          </>
        )
      }
    }

    return {
      disabled: false,
      tooltip: (
        <>
          <Text color="text.secondary" component="span" fontSize="inherit">
            {`${subject} per`}{' '}
          </Text>
          {label}
        </>
      )
    }
  }

  const handleToggle = (option: ShowPerEnum) => {
    onToggle(selected === option ? null : option)

    setShowMore(false)
  }

  return (
    <>
      <SettingsGroup
        helperText={t('metrics', 'distribution_helper_text')}
        title={t('metrics', 'distribution')}
      >
        <SettingsItem disabled={disabled} disabledText={disabledText}>
          <Grid container={true} columns={5}>
            {distributions.map((option) => {
              const state = getOptionState(option.enum)

              return (
                <Grid item={true} key={option.enum} xs={1}>
                  <SettingsToggleOption
                    disabled={state.disabled}
                    iconName={translateShowPerEnum[option.enum].iconName}
                    onClick={() => handleToggle(option.enum)}
                    selected={option.enum === selected}
                    title={translateEnum('show_pers', option.enum).abbreviation}
                    tooltip={state.tooltip}
                  />
                </Grid>
              )
            })}
          </Grid>
        </SettingsItem>
        {filters ? (
          <Collapse in={showPerFilterOptions.length > 0}>
            <Collapse in={showMore}>{filters.content}</Collapse>

            <SettingsItem
              color="info"
              disabled={disabled}
              disabledText={disabledText}
              onClick={() => setShowMore(!showMore)}
              title={
                showMore
                  ? t('widgets', 'hide_benchmark_filters')
                  : t('widgets', 'show_benchmark_filters_with_count', false, {
                    count: String(filters.count)
                  })
              }
              variant="action"
            />
          </Collapse>
        ) : null}
      </SettingsGroup>
    </>
  )
}

export default DistributionSection
